// Runtime feature-flag REGISTRY + read helpers — shared so navigation
// components (AppNavbar, CanvasViewNav, launcher) can gate items via
// featureEnabled(root, key). WRITE/persist lives in the workspace shell
// (@symbo-ls/workspace-shell/functions/featureFlags.js): flags are saved
// per-workspace in Mongo (workspace.settings.featureFlags via
// sdk.updateWorkspaceSettings) so they survive reloads, follow the
// workspace across devices, and stay inspectable by admins on the
// workspace document. Resolution order here (all reactive):
//   1. root.workspace.settings.featureFlags — server truth (Mongo)
//   2. root._featureFlags — live mirror (pre-load hydration + optimistic
//      toggle echo)
//   3. localStorage cache — last-known copy bridging the pre-auth boot
// (2026-07-25 per Nika: /flags + sub-features, per-workspace Mongo
// persistence, gates on all navigation items.)
//
// FA204 / frank trap — every helper/table this file's exports used to
// reference bare (either module-scope locals or same-file sibling
// exports) is unresolvable once each export is individually
// frank.toJSON'd for platform delivery (project_frank_drops_module_local_
// helpers). The ~700-line flag/sub-flag/route tables now live in
// ./featureFlagsData.js and are IMPORTED here — a cross-file import
// resolves fine via frank's globalScope fallback, so this is the fix for
// the data tables without a 6-way duplication of hand-authored content.
// The remaining (cheap) helpers each carry their own self-contained inline
// copy. `_cachedOverrides` is genuine cross-call state (module-memoized,
// invalidated by cacheFeatureFlagOverrides) with no el/root argument to
// hoist it onto — re-homed onto `globalThis.__featureFlagsOverridesCache`,
// the pointerCapability.js/mwtFlag.js template.
import { stripWorkspacePrefix } from './workspacePrefix.js'
import { workspaceSettings } from './workspaceSettings.js'
import { canApp, appKeyForRoute } from './appAccess.js'
import { isCommHost, isCommRoute } from './commHost.js'
import {
  FEATURE_FLAG_GROUPS,
  FEATURE_SUBFLAGS,
  FEATURE_FLAGS,
  FLAG_INDEX,
  ROUTE_FLAGS,
  RAW_FEATURE_FLAGS
} from './featureFlagsData.js'

// Re-exported for backward compatibility — every prior cross-file consumer
// imported these directly from featureFlags.js. Plain pass-through
// re-exports, not function bodies, so unaffected by the trap.
//
// FLAG_INDEX/ROUTE_FLAGS/RAW_FEATURE_FLAGS added 2026-08-13 (tickets/opus.md
// "~800 project-side Frankability defects" — shared's dropped-export bucket).
// Unlike the OTHER dropped-export findings in functions/index.js (bootShell's
// `_causeChain`/`_isTokenRejection`, sdkAdapter's `buildSdkAdapter`,
// notification's `isCrossOrgNotificationHref`/
// `resolveNotificationTarget`, navbarTabMenu's `_resolveWsKey` — all
// documented exceptions, reverted after barrelling caused frank's
// globalScope-lift to reach FUNCTION closures that came out minified), these
// three are plain DATA re-exports (arrays/objects), the exact "unaffected by
// the trap" shape this comment already describes for the three above — safe
// to barrel, verified via `smbls frank to-json .` with no new findings.
export {
  FEATURE_FLAG_GROUPS,
  FEATURE_SUBFLAGS,
  FEATURE_FLAGS,
  FLAG_INDEX,
  ROUTE_FLAGS,
  RAW_FEATURE_FLAGS
}

export const FEATURE_FLAGS_STORAGE_KEY = 'symbols_feature_flags'

// Cached-last-known copy — bridges the window before root.workspace
// lands. Re-homed onto globalThis (see file header); invalidated by
// cacheFeatureFlagOverrides.
export const loadFeatureFlagOverrides = () => {
  const FEATURE_FLAGS_STORAGE_KEY = 'symbols_feature_flags'
  const state = (globalThis.__featureFlagsOverridesCache =
    globalThis.__featureFlagsOverridesCache || { cached: null })
  if (state.cached) return state.cached
  if (typeof window === 'undefined' || !window.localStorage) return {}
  try {
    const raw = window.localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    state.cached = parsed && typeof parsed === 'object' ? parsed : {}
  } catch (_) {
    state.cached = {}
  }
  return state.cached
}

export const cacheFeatureFlagOverrides = (flags) => {
  const FEATURE_FLAGS_STORAGE_KEY = 'symbols_feature_flags'
  const state = (globalThis.__featureFlagsOverridesCache =
    globalThis.__featureFlagsOverridesCache || { cached: null })
  state.cached = flags && typeof flags === 'object' ? flags : {}
  try {
    window.localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(state.cached))
  } catch (_) {
    /* private mode — in-memory cache still applies this session */
  }
}

export const featureFlagDef = (key) => FLAG_INDEX[key]

// The active overrides object, by source priority. Reading BOTH root keys
// keeps gates reactive to (a) the workspace object landing/switching and
// (b) optimistic local toggles.
export const featureFlagOverrides = (root) => {
  // FA204 / frank trap — self-contained inline copy of
  // loadFeatureFlagOverrides, see the file header.
  const _loadFeatureFlagOverrides = () => {
    const FEATURE_FLAGS_STORAGE_KEY = 'symbols_feature_flags'
    const state = (globalThis.__featureFlagsOverridesCache =
      globalThis.__featureFlagsOverridesCache || { cached: null })
    if (state.cached) return state.cached
    if (typeof window === 'undefined' || !window.localStorage) return {}
    try {
      const raw = window.localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY)
      const parsed = raw ? JSON.parse(raw) : null
      state.cached = parsed && typeof parsed === 'object' ? parsed : {}
    } catch (_) {
      state.cached = {}
    }
    return state.cached
  }

  // DECISION site (spec §5) — deliberately NOT gated on `ready`, and this one
  // is the clearest case for not gating: the chain below already degrades to
  // the persisted localStorage cache, which exists precisely so the
  // pre-arrival window resolves to this user's LAST KNOWN flag set rather
  // than to bare defaults. Blocking on `ready` would throw that cache away
  // and make the unloaded window strictly worse, not safer. Reading through
  // the accessor still matters: it stops a list-shaped record from
  // contributing a phantom `featureFlags` key it never had.
  const { settings } = workspaceSettings(root)
  const mirror = root && root._featureFlags
  return settings?.featureFlags || mirror || _loadFeatureFlagOverrides()
}

// THE gate consumers call: `featureEnabled(s.root, 'chat')` or
// `featureEnabled(s.root, 'tasks.standups')`. Core -> always true. A sub
// is on only while its parent is on. Unknown key -> true (never dark-hide
// a live feature by omission).
export const featureEnabled = (root, key) => {
  // FA204 / frank trap — self-contained inline copy of featureFlagOverrides,
  // see the file header. `featureEnabled` recurses on itself for
  // `def.parentKey` — that's a self-reference, not a sibling-export read,
  // so it's left as-is (matches dragRef.js's `setDragRef` self-recursion
  // precedent).
  const _featureFlagOverrides = (root) => {
    const _loadFeatureFlagOverrides = () => {
      const FEATURE_FLAGS_STORAGE_KEY = 'symbols_feature_flags'
      const state = (globalThis.__featureFlagsOverridesCache =
        globalThis.__featureFlagsOverridesCache || { cached: null })
      if (state.cached) return state.cached
      if (typeof window === 'undefined' || !window.localStorage) return {}
      try {
        const raw = window.localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY)
        const parsed = raw ? JSON.parse(raw) : null
        state.cached = parsed && typeof parsed === 'object' ? parsed : {}
      } catch (_) {
        state.cached = {}
      }
      return state.cached
    }
    const { settings } = workspaceSettings(root)
    const mirror = root && root._featureFlags
    return settings?.featureFlags || mirror || _loadFeatureFlagOverrides()
  }

  const def = FLAG_INDEX[key]
  if (!def) return true
  if (def.core) return true
  if (def.parentKey && !featureEnabled(root, def.parentKey)) return false
  const v = _featureFlagOverrides(root)[key]
  return typeof v === 'boolean' ? v : def.defaultOn !== false
}

export const featureFlagForRoute = (path) => {
  const p = stripWorkspacePrefix(String(path || '').split('?')[0])
  if (!p) return null
  const hit = ROUTE_FLAGS.find(([base]) => p === base || p.indexOf(base + '/') === 0)
  return hit ? hit[1] : null
}

// THE navigation gate: `items.filter((it) => navItemVisible(root, it))`.
// Accepts a nav-item object ({ route | path | href, flag? }) or a bare
// path string. Dividers and unmapped items stay visible; `flag: false`
// opts an item out of gating entirely (both the feature-flag AND the
// app-access checks below — an item that opts out of gating opts out of
// all of it).
export const navItemVisible = (root, item) => {
  // NAVBAR-REMOVE-PERSIST-1 — a `{ path, dismissed: true }` row is a
  // persistence TOMBSTONE (moduleConsole.js mcRemoveListItem), not a real
  // destination: it exists only so workspaceConfigs.js's _appendNavbar dedup
  // ('seen' by path) never re-adds an app/module-contributed navbar entry
  // the admin explicitly removed. It must never render.
  if (item && item.dismissed) return false
  // FA204 / frank trap — self-contained inline copies of
  // featureFlagForRoute/featureEnabled/_passesAppAccess, see the file
  // header. `_passesAppAccess` was module-local (used only here) — layered
  // ON TOP of feature flags (see appAccess.js): feature flags are a
  // product on/off switch; app-access is a per-team admin-configurable ACL
  // (the Apps matrix on /admin/teams), only enforced while
  // workspace.settings.appAccessControl.enforced is on. Routes outside the
  // 12-app vocabulary (studio/infra/agents/marketplace/team-members/…)
  // have no app-access key and stay visible; owners/admins always bypass
  // (see canApp).
  const _featureFlagForRoute = (path) => {
    const p = stripWorkspacePrefix(String(path || '').split('?')[0])
    if (!p) return null
    const hit = ROUTE_FLAGS.find(([base]) => p === base || p.indexOf(base + '/') === 0)
    return hit ? hit[1] : null
  }
  const _featureEnabled = (root, key) => {
    const _featureFlagOverrides = (root) => {
      const _loadFeatureFlagOverrides = () => {
        const FEATURE_FLAGS_STORAGE_KEY = 'symbols_feature_flags'
        const state = (globalThis.__featureFlagsOverridesCache =
          globalThis.__featureFlagsOverridesCache || { cached: null })
        if (state.cached) return state.cached
        if (typeof window === 'undefined' || !window.localStorage) return {}
        try {
          const raw = window.localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY)
          const parsed = raw ? JSON.parse(raw) : null
          state.cached = parsed && typeof parsed === 'object' ? parsed : {}
        } catch (_) {
          state.cached = {}
        }
        return state.cached
      }
      const { settings } = workspaceSettings(root)
      const mirror = root && root._featureFlags
      return settings?.featureFlags || mirror || _loadFeatureFlagOverrides()
    }
    const def = FLAG_INDEX[key]
    if (!def) return true
    if (def.core) return true
    if (def.parentKey && !_featureEnabled(root, def.parentKey)) return false
    const v = _featureFlagOverrides(root)[key]
    return typeof v === 'boolean' ? v : def.defaultOn !== false
  }
  const _passesAppAccess = (root, route) => {
    const appKey = appKeyForRoute(route)
    return !appKey || canApp(root, appKey, 'read')
  }

  if (!item) return true
  // THE COMM PROFILE (commHost.js): a comm host (comm.symbols.app) lists only
  // the routes it serves — every other destination lives on the workspace
  // host. Ahead of `flag: false`: an item that opts out of gating still names
  // a route this host does not serve.
  const _commRoute =
    typeof item === 'string' ? item : !item.divider && (item.route || item.path || item.href)
  if (_commRoute && isCommHost() && !isCommRoute(_commRoute)) return false
  if (typeof item === 'string') {
    const flag = _featureFlagForRoute(item)
    if (flag && !_featureEnabled(root, flag)) return false
    return _passesAppAccess(root, item)
  }
  if (item.divider) return true
  if (item.flag === false) return true
  const route = item.route || item.path || item.href
  const flag = item.flag || _featureFlagForRoute(route)
  if (flag && !_featureEnabled(root, flag)) return false
  return _passesAppAccess(root, route)
}
